import React from 'react';
import { makeStyles, withStyles, WithStyles } from '@mui/styles';
import HTTP from 'services/http';
import {
  Grid,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Theme,
} from '@mui/material';
import { buildQaId } from 'utils/build-qa-id';
import { useDispatch } from 'react-redux';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { FillDeliveryConfirmationStatus } from 'interfaces/enums/TaskStatuses/FillDeliveryConfirmationStatus';
import { ShippingStyles } from './shipping.styles';
import { IPrintLabelButton } from './types';
import { printLabel } from './print-label';
import { DeliveryMethodValue } from '../../constants/enums';
import { FDC, EDIT_FILL_DELIVERY_CONFIRMATION } from '../../constants/index';
import { defaultTheme } from '../../lib/themes/arbor-theme-index';

const qaIdBuilder = buildQaId('shipping');

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    backgroundColor: defaultTheme.palette.primary.bluesky,
    marginBottom: 20,
    minWidth: 600,
  },
  btnContainer: {
    marginTop: 30,
  },
  btn: {
    marginRight: 10,
  },
}));

const ModalForm = (props: any): JSX.Element => {
  const { open, submit, title, instructions, cancel } = props;
  const classes = useStyles();
  return (
    <Dialog open={open} onClose={cancel} maxWidth="md">
      <DialogTitle id="simple-dialog-title" className={classes.title}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">{instructions}</Typography>
        <Grid container className={classes.btnContainer}>
          <Grid item xs={6}>
            <Button className={classes.btn} onClick={submit} variant="outlined">
              Yes
            </Button>
            <Button variant="contained" onClick={cancel}>
              No
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

interface IShippingProps extends IPrintLabelButton, WithStyles<typeof ShippingStyles> {}

const ReprintLabelButton = (props: IShippingProps): JSX.Element | null => {
  const {
    classes,
    orderId,
    disabled,
    setPrintLabelClicked,
    currentFdcInformation,
    currentDeliveryInformation,
    pharmacyInfo,
    printDetails,
    isDuplicateOrder,
    updateShippingPage = () => {},
  } = props;
  const [updating, setUpdating] = React.useState(false);
  const dispatch = useDispatch();
  const [showDuplicateOrderNotification, setShowDuplicateOrderNotification] =
    React.useState<boolean>(false);
  const deliveryMethod = currentDeliveryInformation?.deliveryMethod;

  const handleReprint = async () => {
    setUpdating(true);
    try {
      await printLabel({
        orderId,
        dispatch,
        deliveryMethod,
        setPrintLabelClicked,
      });
      let new_status_id = currentFdcInformation?.statusId;
      switch (pharmacyInfo?.workflow_type) {
        case 0: // Simple
          if (
            [
              DeliveryMethodValue.Ups,
              DeliveryMethodValue.FedEx,
              DeliveryMethodValue.Courier,
              DeliveryMethodValue.InternalDelivery,
            ].includes(deliveryMethod as number) &&
            (currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.Packed ||
              currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.WillPickUp)
          ) {
            new_status_id = FillDeliveryConfirmationStatus.Shipped;
          }
          break;
        case 1: // Full
        case 2: // Partial
          if (
            [
              DeliveryMethodValue.Ups,
              DeliveryMethodValue.FedEx,
              DeliveryMethodValue.Courier,
              DeliveryMethodValue.InternalDelivery,
            ].includes(deliveryMethod as number) &&
            (currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.Packed ||
              currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.WillPickUp)
          ) {
            new_status_id = FillDeliveryConfirmationStatus.Shipped;
          }
          if (
            [DeliveryMethodValue.PickUp].includes(deliveryMethod as number) &&
            currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.Packed
          ) {
            new_status_id = FillDeliveryConfirmationStatus.WillPickUp;
          }
          break;
        default:
      }
      const updatedTask = {
        id: currentFdcInformation?.id,
        taskType: FDC,
        therapy_id: currentFdcInformation?.therapyId,
        status_id: new_status_id,
      };
      if (new_status_id !== currentFdcInformation?.statusId) {
        const patchUrl = `/tasks/fdc/${updatedTask.id}`;
        const response = await HTTP.patch(patchUrl, updatedTask, {});
        const reduxPayload = {
          type: EDIT_FILL_DELIVERY_CONFIRMATION,
          payload: response,
        };
        dispatch(reduxPayload);
        dispatch(notifySuccess('FDC was successfully updated'));
      }
    } catch (error) {
      console.log('handleReprint error: ', error);
      const notifyMessage = 'Unable to print label.';
      dispatch(notifyError(notifyMessage));
    } finally {
      setUpdating(false);
      updateShippingPage();
    }
  };
  return (
    <>
      {showDuplicateOrderNotification ? (
        <ModalForm
          open={showDuplicateOrderNotification}
          title="Caution"
          instructions="A shipping label for this order has already been printed. Printing an additional label increases the risk for a HIPAA violation to occur, are you sure you want to reprint?"
          submit={async () => {
            await handleReprint();
            setShowDuplicateOrderNotification(false);
          }}
          cancel={() => setShowDuplicateOrderNotification(false)}
        />
      ) : null}
      {printDetails && (
        <Grid item className={classes.reprintGrid}>
          <Divider />
          <Typography style={{ marginBottom: 15 }} className={classes.reprintLabelText}>
            {printDetails}
          </Typography>
        </Grid>
      )}
      <Grid item className={classes.printLabelButton}>
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            if (!isDuplicateOrder) {
              await handleReprint();
            } else {
              setShowDuplicateOrderNotification(true);
            }
          }}
          data-qa-id={qaIdBuilder('button.reprint-label')}
          disabled={disabled || updating}
        >
          {updating ? '....Processing' : 'Reprint Label'}
        </Button>
      </Grid>
    </>
  );
};

export default withStyles(ShippingStyles)(ReprintLabelButton);
